import { Ship, SearchQuery } from '../types/ship';
import { Vessel, VesselProfile, PSCInspection, SanctionRecord, RiskScore } from '../types/vessel';
import { InspectionAssignment, ChecklistTemplate, InspectionReport, InspectionFinding, AssignmentStatus } from '../types/inspection';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

/** Worker-relative URLs (e.g. owned photo streams) need the API origin. */
export function resolveApiUrl(url: string): string {
  return url.startsWith('/') ? `${API_URL}${url}` : url;
}

/** GET /api/v2/weather — live conditions at a lat/lon (worker-proxied Open-Meteo). */
export interface PositionWeather {
  wind_speed_kn: number | null;
  wind_gusts_kn: number | null;
  wind_direction_deg: number | null;
  temperature_c: number | null;
  visibility_m: number | null;
  weather_code: number | null;
  wave_height_m: number | null;
  wave_direction_deg: number | null;
  wave_period_s: number | null;
  swell_height_m: number | null;
  source: string;
}

/** GET /api/v2/vessels/:id/srp — estimated Paris MoU ship risk profile. */
export interface ShipRiskProfile {
  profile: 'HRS' | 'SRS' | 'LRS-candidate';
  points: number;
  factors: Array<{ id: string; label: string; points: number }>;
  unknowns: string[];
  psc_inspections_36mo: number;
  estimated: boolean;
  method: string;
  official_calculator: string;
}

/** GET /api/v2/vessels/:id/photo — Wikimedia Commons lookup (CC; show credit). */
export interface VesselPhoto {
  found: boolean;
  source?: 'commons' | 'upload';
  url?: string;
  thumb?: string;
  credit?: string;
  license?: string;
  page_url?: string;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  // Pull a fresh token through the auth bridge — Clerk session tokens
  // expire in ~60 s so the localStorage cache is routinely stale.
  const { getFreshToken } = await import('../lib/auth-bridge');
  const token = await getFreshToken();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ─── v1 live AIS ─────────────────────────────────────────────────────────────
export const apiClient = {
  async getShips(): Promise<Ship[]> {
    const r = await apiFetch<{ data: Ship[] }>('/api/ships');
    return r.data ?? [];
  },
  /** Same payload as getShips but returns the freshness envelope too —
   *  used by the map to surface "AIS feed N min ago" + a yellow flag
   *  when the feed has stalled (degraded fallback served stale data). */
  async getShipsWithMeta(): Promise<{
    data: Ship[];
    source: 'd1' | 'mock' | 'mock-fallback' | string;
    count?: number;
    newest_position_age_sec?: number | null;
    newest_position_iso?: string | null;
    warning?: string | null;
  }> {
    const r = await apiFetch<{
      data: Ship[];
      source: string;
      count?: number;
      newest_position_age_sec?: number | null;
      newest_position_iso?: string | null;
      warning?: string | null;
    }>('/api/ships');
    return r;
  },
  async searchShips(query: string, bounds?: [number, number, number, number]): Promise<Ship[]> {
    const payload: SearchQuery = { query, bounds };
    const r = await apiFetch<{ data: Ship[] }>('/api/ships/search', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return r.data ?? [];
  },
  async getShipDetails(mmsi: string): Promise<Ship> {
    const r = await apiFetch<{ data: Ship }>(`/api/ships/${mmsi}`);
    return r.data;
  },
};

// ─── v2 vessel intelligence ───────────────────────────────────────────────────
export const v2 = {
  // Vessel search & profile
  async searchVessels(q: string, params?: { type?: string; flag?: string; page?: number }): Promise<{ data: Vessel[]; count: number }> {
    const p = new URLSearchParams({ q });
    if (params?.type) p.set('type', params.type);
    if (params?.flag) p.set('flag', params.flag);
    if (params?.page) p.set('page', String(params.page));
    return apiFetch(`/api/v2/vessels/search?${p}`);
  },

  /** Live conditions at a position (Open-Meteo via our worker, 30-min cache). */
  async getWeather(lat: number, lon: number): Promise<PositionWeather> {
    const res = await apiFetch<{ data: PositionWeather }>(`/api/v2/weather?lat=${lat}&lon=${lon}`);
    return res.data;
  },

  /** Estimated Paris MoU ship risk profile from held data (labelled estimate). */
  async getVesselSRP(id: string): Promise<ShipRiskProfile> {
    const res = await apiFetch<{ data: ShipRiskProfile }>(`/api/v2/vessels/${id}/srp`);
    return res.data;
  },

  /** Persisted vessel photo: owned upload > Wikimedia Commons; found:false when none. */
  async getVesselPhoto(id: string): Promise<VesselPhoto> {
    const res = await apiFetch<{ data: VesselPhoto }>(`/api/v2/vessels/${id}/photo`);
    return res.data;
  },

  /** Upload an owned vessel photo (raw image ≤5 MB). Replaces any Commons photo. */
  async uploadVesselPhoto(id: string, file: File): Promise<{ ok: boolean; url: string }> {
    const { getFreshToken } = await import('../lib/auth-bridge');
    const token = await getFreshToken();
    const res = await fetch(`${API_URL}/api/v2/vessels/${id}/photo-image`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'image/jpeg',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    if (!res.ok) throw new Error(`${res.status}: ${await res.text().catch(() => res.statusText)}`);
    return (await res.json()).data;
  },

  async getVessel(id: string): Promise<VesselProfile> {
    const res = await apiFetch<{ data: Record<string, unknown> }>(`/api/v2/vessels/${id}`);
    const d = res.data;
    // Remap API field names → frontend type field names
    return {
      ...d,
      risk: d.risk_score,
      recent_inspections: d.psc_inspections,
      recent_port_calls: d.port_calls,
    } as unknown as VesselProfile;
  },

  async getVesselPSC(id: string): Promise<{ summary: { total: number; detentions: number; last_inspection_date: string | null }; inspections: PSCInspection[] }> {
    return apiFetch(`/api/v2/vessels/${id}/psc`);
  },

  async getVesselSanctions(id: string): Promise<{ active: SanctionRecord[]; historical: SanctionRecord[]; has_active: boolean }> {
    return apiFetch(`/api/v2/vessels/${id}/sanctions`);
  },

  async getVesselRisk(id: string): Promise<RiskScore & { vessel_id: string; computed_at: string }> {
    return apiFetch(`/api/v2/vessels/${id}/risk`);
  },

  async getVesselPortCalls(id: string): Promise<{ data: unknown[]; count: number }> {
    return apiFetch(`/api/v2/vessels/${id}/port-calls`);
  },

  // Inspection assignments
  async listAssignments(params?: { status?: string; page?: number }): Promise<{ data: InspectionAssignment[]; count: number }> {
    const p = new URLSearchParams();
    if (params?.status) p.set('status', params.status);
    if (params?.page)   p.set('page', String(params.page));
    return apiFetch(`/api/v2/assignments?${p}`);
  },

  async createAssignment(body: Partial<InspectionAssignment>): Promise<InspectionAssignment> {
    const res = await apiFetch<{ data: InspectionAssignment }>('/api/v2/assignments', { method: 'POST', body: JSON.stringify(body) });
    return res.data;
  },

  async getAssignment(id: string): Promise<InspectionAssignment & { checklist?: ChecklistTemplate }> {
    const res = await apiFetch<{ data: InspectionAssignment & { checklist?: ChecklistTemplate } }>(`/api/v2/assignments/${id}`);
    return res.data;
  },

  async updateAssignmentStatus(id: string, status: AssignmentStatus): Promise<InspectionAssignment> {
    return apiFetch(`/api/v2/assignments/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
  },

  async generateAssignmentPackPdf(id: string): Promise<Blob> {
    const token = localStorage.getItem('clerk_token');
    const res = await fetch(`${API_URL}/api/v2/assignments/${id}/pack/pdf`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`PDF generation failed: ${res.status}`);
    return res.blob();
  },

  // Checklists
  async listChecklists(): Promise<{ data: ChecklistTemplate[] }> {
    return apiFetch('/api/v2/checklists');
  },

  async getChecklist(id: string): Promise<ChecklistTemplate> {
    const res = await apiFetch<{ data: ChecklistTemplate }>(`/api/v2/checklists/${id}`);
    return res.data;
  },

  // Reports
  async createReport(body: { assignment_id: string; template_id?: string }): Promise<InspectionReport> {
    const res = await apiFetch<{ data: InspectionReport }>('/api/v2/reports', { method: 'POST', body: JSON.stringify(body) });
    return res.data;
  },

  async getReport(id: string): Promise<InspectionReport> {
    const res = await apiFetch<{ data: InspectionReport }>(`/api/v2/reports/${id}`);
    return res.data;
  },

  async addFinding(reportId: string, finding: Partial<InspectionFinding>): Promise<InspectionFinding> {
    return apiFetch(`/api/v2/reports/${reportId}/findings`, { method: 'POST', body: JSON.stringify(finding) });
  },

  async updateFinding(reportId: string, findingId: string, patch: Partial<InspectionFinding>): Promise<InspectionFinding> {
    return apiFetch(`/api/v2/reports/${reportId}/findings/${findingId}`, { method: 'PUT', body: JSON.stringify(patch) });
  },

  async syncOffline(reportId: string, findings: InspectionFinding[]): Promise<{ synced: number }> {
    return apiFetch(`/api/v2/reports/${reportId}/sync`, { method: 'POST', body: JSON.stringify({ findings }) });
  },

  async submitReport(reportId: string): Promise<{ report_id: string; status: string; finding_count: number }> {
    return apiFetch(`/api/v2/reports/${reportId}/submit`, { method: 'POST' });
  },

  // Evidence upload
  async getUploadUrl(params: { report_id: string; finding_id: string; filename: string; content_type: string }): Promise<{ upload_key: string; upload_endpoint: string }> {
    return apiFetch('/api/v2/evidence/upload-url', { method: 'POST', body: JSON.stringify(params) });
  },

  async uploadEvidence(endpoint: string, file: File): Promise<void> {
    const token = localStorage.getItem('clerk_token');
    await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'Content-Length': String(file.size),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
  },

  // Analytics
  async getAnalyticsSummary(): Promise<unknown> {
    return apiFetch('/api/analytics/summary');
  },

  async getDailyStats(days = 30): Promise<unknown> {
    return apiFetch(`/api/analytics/stats/daily?days=${days}`);
  },

  // Admin jobs
  async triggerJob(jobName: string): Promise<unknown> {
    return apiFetch(`/api/v2/admin/jobs/${jobName}`, { method: 'POST' });
  },

  async listJobs(): Promise<{ data: unknown[] }> {
    return apiFetch('/api/v2/admin/jobs');
  },

  // Auth
  async getMe(): Promise<{ data: unknown }> {
    return apiFetch('/api/v2/auth/me');
  },

  // Admin: user management
  async listUsers(queryString = ''): Promise<{ data: unknown[] }> {
    return apiFetch(`/api/v2/admin/users${queryString ? '?' + queryString : ''}`);
  },

  async updateUserRole(userId: string, role: string): Promise<unknown> {
    return apiFetch(`/api/v2/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  },

  async updateUserStatus(userId: string, status: string): Promise<unknown> {
    return apiFetch(`/api/v2/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
};
